import { useCallback, useEffect, useState } from "react";
import { Link2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { AskEvaButton } from "@/components/AskEvaButton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { JT_BASE_URL } from "@/components/jobtimehr/constants";
import { StatusCards } from "@/components/jobtimehr/StatusCards";
import { ConnectionSection } from "@/components/jobtimehr/ConnectionSection";
import { OverviewTab } from "@/components/jobtimehr/OverviewTab";
import { SyncTab } from "@/components/jobtimehr/SyncTab";
import { MappingTab } from "@/components/jobtimehr/MappingTab";
import { StagingTab } from "@/components/jobtimehr/StagingTab";
import { ConnectWizardDialog } from "@/components/jobtimehr/ConnectWizardDialog";
import {
  DisconnectDialog, RotateDialog, SyncDetailDialog, MappingDialog,
} from "@/components/jobtimehr/JobtimehrDialogs";

export default function Jobtimehr() {
  const { companyId, company, period, can } = useTenant();
  const [arch, setArch] = useState(null);
  const [connection, setConnection] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [syncs, setSyncs] = useState([]);
  const [currentSync, setCurrentSync] = useState(null);
  const [conflicts, setConflicts] = useState([]);
  const [staging, setStaging] = useState([]);
  const [logs, setLogs] = useState([]);
  const [includeUnknown, setIncludeUnknown] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncDetail, setSyncDetail] = useState(null);
  // wizard connessione
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [connForm, setConnForm] = useState({ base_url: JT_BASE_URL, client_id: "", client_secret: "", company_external_id: "", sync_frequency: "MANUAL" });
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [rotateOpen, setRotateOpen] = useState(false);
  const [rotateForm, setRotateForm] = useState({ client_id: "", client_secret: "" });
  const [mapOpen, setMapOpen] = useState(false);
  const [mapForm, setMapForm] = useState({ source_system: "JOBTIMEHR", source_field: "", transformation: "DIRECT", canonical_metric: "", notes: "" });

  const canImport = can("social:import");
  const canMappingWrite = can("jobtimehr:mapping:write");
  const canResolve = can("social:approve");
  const isConnected = connection && ["CONNECTED", "DEGRADED", "AUTH_ERROR", "API_ERROR"].includes(connection.status);

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      const [a, c, l, conn] = await Promise.all([
        api.get(`/companies/${companyId}/jobtimehr/architecture`),
        api.get(`/companies/${companyId}/jobtimehr/catalog`),
        api.get(`/companies/${companyId}/jobtimehr/logs`),
        api.get(`/companies/${companyId}/jobtimehr/connection`),
      ]);
      setArch(a.data);
      setCatalog(c.data);
      setLogs(l.data);
      setConnection(conn.data);
    } catch (err) {
      toast.error("Errore caricamento", { description: formatApiError(err) });
    }
    try {
      const { data } = await api.get(`/companies/${companyId}/jobtimehr/mappings`);
      setMappings(data);
    } catch { setMappings([]); }
    try {
      const { data } = await api.get(`/companies/${companyId}/jobtimehr/conflicts`);
      setConflicts(data);
    } catch { setConflicts([]); }
    if (period?.id) {
      try {
        const { data } = await api.get(`/companies/${companyId}/periods/${period.id}/jobtimehr/syncs`);
        setSyncs(data);
      } catch { setSyncs([]); }
      try {
        const { data } = await api.get(`/companies/${companyId}/periods/${period.id}/jobtimehr/staging`);
        setStaging(data);
      } catch { setStaging([]); }
    }
  }, [companyId, period?.id]);

  useEffect(() => { load(); }, [load]);

  // ---------- wizard connessione ----------
  const runConnectTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post(`/companies/${companyId}/jobtimehr/connect/test`, {
        base_url: connForm.base_url, client_id: connForm.client_id,
        client_secret: connForm.client_secret, company_external_id: connForm.company_external_id,
      });
      setTestResult(data);
      if (data.result === "SUCCESS") setWizardStep(2);
    } catch (err) {
      setTestResult({ result: "API UNREACHABLE", detail: formatApiError(err) });
    } finally {
      setTesting(false);
    }
  };

  const confirmConnect = async () => {
    try {
      await api.post(`/companies/${companyId}/jobtimehr/connect`, {
        ...connForm, api_version: "v1",
      });
      toast.success("JOBTIMEHR collegato con successo");
      setWizardOpen(false);
      setWizardStep(1);
      setConnForm({ base_url: JT_BASE_URL, client_id: "", client_secret: "", company_external_id: "", sync_frequency: "MANUAL" });
      setTestResult(null);
      load();
    } catch (err) {
      toast.error("Connessione fallita", { description: formatApiError(err) });
    }
  };

  const disconnect = async () => {
    try {
      const { data } = await api.post(`/companies/${companyId}/jobtimehr/disconnect`);
      toast.success("JOBTIMEHR scollegato", { description: data.message });
      setDisconnectOpen(false);
      load();
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };

  const rotateCredentials = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/companies/${companyId}/jobtimehr/rotate-credentials`, rotateForm);
      toast.success("Credenziali aggiornate (test superato)");
      setRotateOpen(false);
      setRotateForm({ client_id: "", client_secret: "" });
      load();
    } catch (err) {
      toast.error("Rotazione fallita", { description: formatApiError(err) });
    }
  };

  // ---------- sync ----------
  const runMockSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(
        `/companies/${companyId}/periods/${period.id}/jobtimehr/mock-sync`,
        { include_unknown_contract: includeUnknown });
      setCurrentSync(data);
      toast.success("Sync MOCK completata — rivedi l'anteprima prima di applicare");
      load();
    } catch (err) {
      toast.error("Errore sync", { description: formatApiError(err) });
    } finally {
      setSyncing(false);
    }
  };

  const runRealSync = async (mode) => {
    setSyncing(true);
    try {
      const { data } = await api.post(
        `/companies/${companyId}/periods/${period.id}/jobtimehr/real-sync`, { mode });
      setCurrentSync(data);
      if (data.locked_preview) {
        toast.info("Periodo bloccato: anteprima disponibile, applicazione non consentita");
      } else {
        toast.success(`Sync reale ${data.status} — rivedi l'anteprima`);
      }
      load();
    } catch (err) {
      toast.error("Sync fallita", { description: formatApiError(err) });
    } finally {
      setSyncing(false);
    }
  };

  const applySync = async (syncId) => {
    try {
      const { data } = await api.post(
        `/companies/${companyId}/periods/${period.id}/jobtimehr/syncs/${syncId}/apply`);
      toast.success(`Sync applicata: ${data.applied} metriche importate, ${data.conflicts} conflitti aperti`);
      setCurrentSync(null);
      load();
    } catch (err) {
      toast.error("Errore applicazione", { description: formatApiError(err) });
    }
  };

  const openSyncDetail = async (syncId) => {
    try {
      const { data } = await api.get(
        `/companies/${companyId}/periods/${period.id}/jobtimehr/syncs/${syncId}`);
      setSyncDetail(data);
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };

  const resolveConflict = async (id, resolution) => {
    try {
      await api.post(`/companies/${companyId}/jobtimehr/conflicts/${id}/resolve`, { resolution });
      toast.success(resolution === "USE_INCOMING" ? "Valore importato (stato: da verificare)" : "Valore attuale mantenuto");
      load();
    } catch (err) {
      toast.error("Errore risoluzione", { description: formatApiError(err) });
    }
  };

  const createMapping = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/companies/${companyId}/jobtimehr/mappings`, mapForm);
      toast.success("Mapping creato");
      setMapOpen(false);
      setMapForm({ source_system: "JOBTIMEHR", source_field: "", transformation: "DIRECT", canonical_metric: "", notes: "" });
      load();
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };

  const deleteMapping = async (id) => {
    try {
      await api.delete(`/companies/${companyId}/jobtimehr/mappings/${id}`);
      toast.success("Mapping eliminato");
      load();
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };

  return (
    <div className="space-y-6" data-testid="jobtimehr-page">
      <div>
        <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
          <Link2 className="w-7 h-7 text-green-700" aria-hidden="true" /> Integrazione JOBTIMEHR
        </h1>
        <AskEvaButton question="Ci sono problemi nella sincronizzazione JOBTIMEHR?" />
        </div>
        <p className="text-sm text-slate-600 mt-1 max-w-3xl leading-relaxed">
          JOBTIMEHR è il sistema HR operativo; ESG PMI resta il sistema ESG. Le due piattaforme comunicano
          tramite API read-only controllata: staging → mapping → dati canonici → Social Engine → VSME.
        </p>
      </div>

      <StatusCards arch={arch} isConnected={isConnected} />

      <ConnectionSection
        isConnected={isConnected} connection={connection} company={company} companyId={companyId}
        period={period} canMappingWrite={canMappingWrite} syncing={syncing}
        onOpenWizard={() => { setWizardOpen(true); setWizardStep(1); setTestResult(null); }}
        onRealSync={runRealSync}
        onOpenRotate={() => setRotateOpen(true)}
        onOpenDisconnect={() => setDisconnectOpen(true)}
      />

      <Tabs defaultValue="panoramica">
        <TabsList className="flex flex-wrap h-auto gap-1" data-testid="jt-tabs">
          <TabsTrigger value="panoramica" data-testid="tab-jt-panoramica">Panoramica</TabsTrigger>
          <TabsTrigger value="sync" data-testid="tab-jt-sync">Sincronizzazioni</TabsTrigger>
          <TabsTrigger value="mapping" data-testid="tab-jt-mapping">Mapping</TabsTrigger>
          <TabsTrigger value="staging" data-testid="tab-jt-staging">Staging & Log</TabsTrigger>
        </TabsList>

        <TabsContent value="panoramica" className="space-y-4 mt-4">
          <OverviewTab arch={arch} catalog={catalog} isConnected={isConnected} />
        </TabsContent>

        <TabsContent value="sync" className="space-y-4 mt-4">
          <SyncTab
            includeUnknown={includeUnknown} onIncludeUnknownChange={setIncludeUnknown}
            canImport={canImport} syncing={syncing} period={period} onMockSync={runMockSync}
            currentSync={currentSync} onApplySync={applySync} onCancelPreview={() => setCurrentSync(null)}
            conflicts={conflicts} canResolve={canResolve} onResolveConflict={resolveConflict}
            syncs={syncs} onOpenSyncDetail={openSyncDetail}
          />
        </TabsContent>

        <TabsContent value="mapping" className="space-y-4 mt-4">
          <MappingTab
            canMappingWrite={canMappingWrite} mappings={mappings}
            onOpenMap={() => setMapOpen(true)} onDeleteMapping={deleteMapping}
          />
        </TabsContent>

        <TabsContent value="staging" className="space-y-4 mt-4">
          <StagingTab staging={staging} logs={logs} />
        </TabsContent>
      </Tabs>

      <ConnectWizardDialog
        open={wizardOpen} onOpenChange={setWizardOpen}
        wizardStep={wizardStep} onWizardStepChange={setWizardStep}
        connForm={connForm} onConnFormChange={setConnForm}
        testResult={testResult} testing={testing}
        onTest={runConnectTest} onConfirm={confirmConnect}
        company={company} companyId={companyId}
      />

      <DisconnectDialog open={disconnectOpen} onOpenChange={setDisconnectOpen} onConfirm={disconnect} />

      <RotateDialog
        open={rotateOpen} onOpenChange={setRotateOpen}
        rotateForm={rotateForm} onRotateFormChange={setRotateForm} onSubmit={rotateCredentials}
      />

      <SyncDetailDialog syncDetail={syncDetail} onClose={() => setSyncDetail(null)} />

      <MappingDialog
        open={mapOpen} onOpenChange={setMapOpen}
        mapForm={mapForm} onMapFormChange={setMapForm} onSubmit={createMapping} catalog={catalog}
      />
    </div>
  );
}
